import { useCallback, useEffect } from 'react';
import { isEmpty } from 'lodash';

import useDataFetcher from '~/components/useDataFetcher';
import type { ClaimConductorHandlingResponse } from '~/server_shared/openapi/data-contracts';

/** The reload of every mounted reader, by the claim it reads. */
const readersByClaim = new Map<number, Set<() => Promise<unknown>>>();

export interface ConductorHandling {
  /** Null until the first read lands, and where it failed. A failed re-read keeps the handling it had. */
  handling: ClaimConductorHandlingResponse | null;
  /** Reads the handling again here and in every other mounted reader of the same claim. */
  reloadHandling: () => Promise<void>;
}

/**
 * Who drives the claim, Clive or a handler who took it over, as every part of the claim page that shows it
 * reads it. A switch made in one is read again in all of them, without a reload. A switch made in another
 * session is not, until the reader remounts or switches the claim itself.
 *
 * No error dialog: a claim on which Clive chat is unavailable refuses the read, and each reader draws the
 * claim's handling as unknown instead.
 */
export const useConductorHandling = (claimId: number): ConductorHandling => {
  const { data, reloadData } = useDataFetcher<ClaimConductorHandlingResponse>(
    `/api/v1/claims/${claimId}/conductor/handling`,
    null,
    { shouldOpenErrorDialog: false }
  );

  useEffect(() => {
    const readers = readersByClaim.get(claimId) ?? new Set();
    readers.add(reloadData);
    readersByClaim.set(claimId, readers);

    return () => {
      readers.delete(reloadData);
      if (isEmpty(readers)) readersByClaim.delete(claimId);
    };
  }, [claimId, reloadData]);

  const reloadHandling = useCallback(async (): Promise<void> => {
    await Promise.all(Array.from(readersByClaim.get(claimId) ?? [], (reload) => reload()));
  }, [claimId]);

  return { handling: data ?? null, reloadHandling };
};
